/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2021 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import { inject } from "mobx-react";
import { ReadAloudOverlay } from "components/readAloud/ReadAloudOverlay";
import { getCurrentWordRect, getCurrentNodeRect, getPageIndex, getCurrentNode } from "selectors/readAloudSelectors";
import { getPositionFromRect } from "utils/ReadAloudUtils";
import { getViewRotationAngle } from "selectors/documentSelectors";
import { isHighContrastEnabled } from "selectors/highContrastSelectors";

const mapStoreToProps = (stores, ownProps) => {
  const isHCEnabled = isHighContrastEnabled(stores);
  const defaultOverlayStyle = {
    position: "absolute",
    zIndex: 1000,
    ...(isHCEnabled
      ? {
          border: "1px solid Highlight",
          forcedColorAdjust: "none",
          opacity: 1
        }
      : {
          backgroundColor: "#d3d3d3",
          mixBlendMode: "multiply",
          opacity: 0.25
        })
  };

  const wordRect = getCurrentWordRect(stores),
    nodeRect = getCurrentNodeRect(stores),
    pageIndex = getPageIndex(stores),
    viewRotationAngle = getViewRotationAngle(stores);

  let divStyles = [];
  if (wordRect && ownProps.pageIndex === pageIndex) {
    const { top, bottom, left, right } = getPositionFromRect(wordRect, ownProps.pageIndex, stores),
      { top: nodeTop, bottom: nodeBottom, left: nodeLeft, right: nodeRight } = getPositionFromRect(
        nodeRect,
        ownProps.pageIndex,
        stores
      ),
      node = getCurrentNode(stores),
      isRotated = viewRotationAngle === 90 || viewRotationAngle === 270;
    let lineStyle, wordDiv, lineDiv;

    const wordOpacity = isHCEnabled ? 1 : 0.5;
    const lineBorder = isHCEnabled ? "1px dashed CanvasText" : "none";
    if (node.isVertical !== isRotated) {
      lineStyle = { ...defaultOverlayStyle, left, width: right - left };
      wordDiv = { ...lineStyle, top, height: bottom - top, opacity: wordOpacity };
      lineDiv = { ...lineStyle, top: nodeTop, height: nodeBottom - nodeTop, border: lineBorder };
    } else {
      lineStyle = { ...defaultOverlayStyle, top, height: bottom - top };
      wordDiv = { ...lineStyle, left, width: right - left, opacity: wordOpacity };
      lineDiv = { ...lineStyle, left: nodeLeft, width: nodeRight - nodeLeft, border: lineBorder };
    }
    divStyles = [wordDiv, lineDiv];
  }

  return { divStyles };
};

const ReadAloudOverlayContainer = inject(mapStoreToProps)(ReadAloudOverlay);
export { ReadAloudOverlayContainer };
